import * as React from 'react'

import { useQuery } from '@apollo/client/react'

import { setSentryUser } from '~publish/components/ErrorBoundary/PublishSentryClient'
import { graphql } from '~publish/graphql'
import NotificationActionCreators from '~publish/legacy/composer/composer/action-creators/NotificationActionCreators'
import { NotificationScopes } from '~publish/legacy/composer/composer/AppConstants'
import type { AppDispatch } from '~publish/legacy/store'

import type { AccountContextValue } from './types'

export const AccountContext = React.createContext<AccountContextValue | null>(
  null,
)

export const AccountContextProvider: React.FC<{
  children: React.ReactNode
  // dispatch is needed to avoid importing all of the store
  // which makes some tests fail because of import cycles
  dispatch: AppDispatch
}> = ({ children, dispatch }) => {
  const {
    loading,
    data: account,
    refetch: refetchAccount,
  } = useQuery(GetAccount)

  const { ORGANIZATION_EVENT_KEY } = window?.appshell?.eventKeys || {}
  const { billingUpdated } = window?.appshell?.actionKeys || {}

  React.useEffect(() => {
    setSentryUser(account?.account?.id ?? null)
  }, [account?.account?.id])

  React.useEffect(() => {
    function onOrgChange({ detail }: { detail: { action: string } }): void {
      if (!detail.action) {
        void refetchAccount()
        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      } else if (detail.action === billingUpdated) {
        NotificationActionCreators.removeAllNotificationsByScopePrefix(
          `${NotificationScopes.PROFILE_QUEUE_LIMIT}-`,
        )
        void refetchAccount()
        dispatch({ type: 'INIT_ORGANIZATIONS' })
      }
    }
    window.addEventListener(ORGANIZATION_EVENT_KEY, onOrgChange)

    return function cleanup(): void {
      window.removeEventListener(ORGANIZATION_EVENT_KEY, onOrgChange)
    }
  }, [refetchAccount, ORGANIZATION_EVENT_KEY, billingUpdated, dispatch])

  const accountData = account?.account ?? null
  // Only report the first load. `account.account` contains the usable value.
  // A background refresh can keep cached data and must not restart loading states.
  const isLoading = loading && !accountData
  const accountContextValue = React.useMemo<AccountContextValue>(
    () => ({ account: accountData, loading: isLoading, refetchAccount }),
    [accountData, isLoading, refetchAccount],
  )

  return (
    <AccountContext.Provider value={accountContextValue}>
      {children}
    </AccountContext.Provider>
  )
}

export const GetAccount = graphql(/* GraphQL */ `
  query GetAccount {
    account {
      __typename
      id
      name
      email
      products {
        name
        userId
      }
      backupEmail
      hasVerifiedEmail
      hasVerifiedBackupEmail
      avatar
      featureFlips
      createdAt
      dismissedBanners
      isImpersonation
      tfa {
        type
      }
      timezone
      preferences {
        timeFormat
        startOfWeek
        defaultScheduleOption
        landingPage
        community {
          mentionsCounter
        }
      }
      settings {
        aiSettings {
          onboarded
        }
      }
      organizations {
        id
        name
        settings {
          enforce2FA
        }
        shouldEnforce2FASetup
      }
      currentOrganization {
        __typename
        id
        allowedActions
        isOneBufferOrganization
        isBetaEnabled
        isRolesEnabled
        entitlements
        createdAt
        ownerEmail
        members {
          totalCount
        }
        settings {
          __typename
          enforce2FA
        }
        shouldEnforce2FASetup
        channels {
          id
          name
          service
          products
          organizationId
          isLocked
          allowedActions
          descriptor
          avatar
          showTrendingTopicSuggestions
          metadata {
            ... on MastodonMetadata {
              maxCharacters
            }
          }
        }
        commonTrackingProperties {
          atEventBillingState
          atEventBillingPlan
          atEventBillingCycle
          atEventBillingGateway
          atEventIsNewBuffer
          atEventChannelQty
        }
        dismissedObjects {
          id
        }
        name
        role
        billing {
          __typename
          canAccessPublishing
          ... on OBBilling {
            id
            canStartTrial
            subscription {
              __typename
              interval
              plan {
                __typename
                id
              }
              trial {
                isActive
              }
            }
            gateway {
              __typename
              gatewayType
            }
            paymentDetails {
              hasPaymentDetails
              paymentMethod
            }
            pricingTiers {
              flatFee
              unitPrice
              upperBound
            }
          }
        }
        limits {
          __typename
          ideas
          channels
          scheduledPosts
          tags
          savedReplies
          postTemplates
        }
      }
    }
  }
`)
